import * as React from "react";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";

import { SxProps, Theme } from "@mui/material/styles";
import { useAppSelector } from "../../../../app/hooks";
import { selectDeviceById } from "../../../device/deviceSlice";
import { selectStructureNodeById } from "../../../device/structureSlice";
import { selectSettings } from "../../../settings/settingsSlice";
import { ValidationObjectType } from "../validationProcessApi";


// --- typy ---

export interface DocumentTargetInfoProps {
    objectId?: string | number | null;
    objectType?: ValidationObjectType | null;
    processNumber?: string | number | null;
    processTitle?: string | null;
    structureId?: string | number | null;
    sx?: SxProps<Theme>;
}

// Pomocnicze etykiety
const LABEL_BY_TYPE: Record<ValidationObjectType, string> = {
    DEVICE: "Urządzenie",
    ROOM: "Pomieszczenie",
    COMPUTERIZED_SYSTEM: "System skomputeryzowany",
};

export default function DocumentTargetInfo({
    objectId,
    objectType,
    processNumber,
    processTitle,
    structureId,
    sx,
}: DocumentTargetInfoProps) {
    const { locale } = useAppSelector(selectSettings);
    const lang = locale === "pl" ? "pl" : "en";

    const structure = useAppSelector((state) =>
        structureId ? selectStructureNodeById(state, structureId) : null
    );

    const structureDisplay = React.useMemo(() => {
        const nPl = structure?.name ?? "";
        const nEN = structure?.name_EN ?? "";
        const nm = lang === "pl" ? (nPl || nEN) : (nEN || nPl);
        const code = structure?.code ? ` (${structure.code})` : "";
        return (nm || structure?.code) ? `${nm}${code}` : "-";
    }, [structure?.name, structure?.name_EN, structure?.code, lang]);

    // Pobranie encji na podstawie typu i ID
    const entity = useAppSelector((state) => {
        if (!objectType || objectId == null) return null;

        switch (objectType) {
            case "DEVICE":
                return selectDeviceById(state, objectId);
            // case "ROOM":
            //     return selectRoomById(state, objectId);
            case "COMPUTERIZED_SYSTEM":
                return ""
            default:
                return null;
        }
    });

    const objectLabel = objectType ? LABEL_BY_TYPE[objectType] : "Obiekt";

    const renderEntityDisplay = () => {
        if (!entity) return "-";

        if (objectType === "DEVICE") {
            const d = entity as { id: string | number; name?: string; inventoryNo?: string };
            return d.name ? `${d.name}${d.inventoryNo ? ` (${d.inventoryNo})` : ""}` : d.id;
        }

        if (objectType === "ROOM") {
            const r = entity as { id: string | number; code?: string; name?: string };
            return r.code ? `${r.code}${r.name ? ` – ${r.name}` : ""}` : r.id;
        }

        if (objectType === "COMPUTERIZED_SYSTEM") {
            const s = entity as { id: string | number; title?: string; version?: string };
            return s.title ? `${s.title}${s.version ? ` v${s.version}` : ""}` : s.id;
        }

        // Fallback
        return objectId ?? "-";
    };

    return (
        <Box
            sx={(t) => ({
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                p: 2,
                mb: 2,
                border: `1px solid ${t.palette.divider}`,
                borderRadius: 1.5,
                bgcolor: t.palette.action.hover,
                ...((typeof sx === "function" ? sx(t) : sx) as object),
            })}
        >
            <InfoOutlinedIcon sx={{ mt: 1, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Dokument zostanie dodany do:
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        rowGap: 0.5,
                        columnGap: 1,
                    }}
                >
                    {/* Proces */}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                        <AssignmentOutlinedIcon fontSize="small" />
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            Proces:
                        </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {processTitle || "-"}
                    </Typography>

                    {/* Numer procesu – osobna etykieta pod spodem */}

                    <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <TagOutlinedIcon fontSize="small" aria-hidden />

                        <Box component="span" sx={{ fontWeight: 600 }}>
                            Numer procesu:
                        </Box>
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {processNumber ? (
                            <>

                                <Chip size="small" variant="outlined" label={`${processNumber}`} sx={{ fontFamily: "monospace" }} />
                                <Tooltip title="Kopiuj numer procesu">
                                    <IconButton
                                        size="small"
                                        onClick={() => navigator.clipboard.writeText(String(processNumber))}
                                        aria-label="Kopiuj numer procesu"
                                    >
                                        <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.disabled">brak</Typography>
                        )}
                    </Stack>

                    {/* Obiekt */}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                        <DevicesOtherOutlinedIcon fontSize="small" />
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            {objectLabel}:
                        </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {objectId ? renderEntityDisplay() : "-"}
                    </Typography>

                    {/* Struktura */}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                        <ApartmentOutlinedIcon fontSize="small" />
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            Struktura:
                        </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {structureDisplay ?? "-"}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
